import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Target, CheckCircle, Clock, Zap, Gift, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const Challenges = () => {
  const [dailyChallenges, setDailyChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const response = await api.get('/challenges/daily');
      setDailyChallenges(response.data.challenges || []);
    } catch (error) {
      console.error('Error fetching challenges:', error);
      // Set default challenges if API fails
      setDailyChallenges([
        { id: 1, name: 'Triple Check', description: 'Log glucose 3 times today', progress: 1, target: 3, points: 15, icon: '💉', completed: false },
        { id: 2, name: 'Meal Master', description: 'Log all 3 meals today', progress: 2, target: 3, points: 15, icon: '🍽️', completed: false },
        { id: 3, name: 'Hydration Hero', description: 'Log 8 glasses of water', progress: 5, target: 8, points: 10, icon: '💧', completed: false },
        { id: 4, name: 'Step It Up', description: 'Walk 5,000 steps', progress: 3200, target: 5000, points: 20, icon: '🚶', completed: false },
        { id: 5, name: 'Recipe Adventurer', description: 'Try a new recipe', progress: 0, target: 1, points: 10, icon: '👨‍🍳', completed: false },
        { id: 6, name: 'Target Zone', description: 'Keep all readings in range', progress: 1, target: 1, points: 25, icon: '🎯', completed: true },
      ]);
    }
    setLoading(false);
  };

  const handleClaimReward = async (challengeId) => {
    try {
      await api.post(`/challenges/${challengeId}/claim`);
      toast({
        title: '🎉 Reward Claimed!',
        description: 'Points have been added to your account.',
      });
      fetchChallenges();
    } catch (error) {
      toast({
        title: 'Already Claimed',
        description: 'You have already claimed this reward.',
        variant: 'destructive'
      });
    }
  };

  const completedCount = dailyChallenges.filter(c => c.completed).length;
  const totalPoints = dailyChallenges.reduce((sum, c) => sum + (c.completed ? c.points : 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Daily Challenges</h1>
          <p className="text-gray-600 dark:text-gray-400">Complete challenges to earn points and badges</p>
        </div>
        <Button
          variant="outline"
          onClick={() => { setRefreshing(true); fetchChallenges().then(() => setRefreshing(false)); }}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Progress Summary */}
      <Card className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Today's Progress</p>
              <p className="text-3xl font-bold">{completedCount}/{dailyChallenges.length} Completed</p>
              <p className="text-purple-200 mt-1">+{totalPoints} points earned today</p>
            </div>
            <div className="text-right">
              <Gift className="w-16 h-16 opacity-80" />
              <p className="text-sm text-purple-200 mt-2">Resets in 12h</p>
            </div>
          </div>
          <Progress value={(completedCount / dailyChallenges.length) * 100} className="mt-4 h-3 bg-purple-400" />
        </CardContent>
      </Card>

      {/* Challenges List */}
      <div className="grid gap-4">
        {dailyChallenges.map((challenge) => {
          const progressPercent = Math.min((challenge.progress / challenge.target) * 100, 100);
          const isComplete = challenge.completed || challenge.progress >= challenge.target;
          
          return (
            <Card key={challenge.id} className={`dark:bg-gray-800 transition-all ${
              isComplete ? 'border-green-500 border-2' : ''
            }`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className={`text-4xl p-3 rounded-xl ${
                    isComplete ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    {challenge.icon}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{challenge.name}</h3>
                      <Badge className={isComplete ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                        +{challenge.points} XP
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{challenge.description}</p>
                    
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600 dark:text-gray-400">
                          {challenge.progress} / {challenge.target}
                        </span>
                        <span className={isComplete ? 'text-green-600' : 'text-gray-600 dark:text-gray-400'}>
                          {Math.round(progressPercent)}%
                        </span>
                      </div>
                      <Progress value={progressPercent} className={`h-2 ${isComplete ? 'bg-green-200' : ''}`} />
                    </div>
                  </div>

                  {isComplete ? (
                    <Button
                      onClick={() => handleClaimReward(challenge.id)}
                      className="bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Claim
                    </Button>
                  ) : (
                    <div className="text-center">
                      <Clock className="w-6 h-6 text-gray-400 mx-auto" />
                      <span className="text-xs text-gray-500">In Progress</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Bonus Challenge */}
      <Card className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6" />
                <h3 className="text-xl font-bold">Bonus Challenge</h3>
              </div>
              <p className="mt-2">Complete ALL daily challenges to earn 50 bonus XP!</p>
            </div>
            {completedCount === dailyChallenges.length ? (
              <Button className="bg-white text-orange-600 hover:bg-orange-50">
                Claim 50 XP
              </Button>
            ) : (
              <div className="text-right">
                <p className="text-2xl font-bold">{dailyChallenges.length - completedCount}</p>
                <p className="text-orange-100">remaining</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Challenges;
